import { addSwipe } from '../../utils/swipe';

const INIT_FLAG = 'stgbSliderInit';

const initSlider = (root: HTMLElement) => {
    if (root.dataset[INIT_FLAG] === 'true') return;

    const track = root.querySelector<HTMLElement>('.stgb-slider-track');
    const slides = Array.from(root.querySelectorAll<HTMLElement>('.stgb-slider-slide'));
    const dots = Array.from(root.querySelectorAll<HTMLButtonElement>('.stgb-slider-dot'));
    const prevBtn = root.querySelector<HTMLButtonElement>('.stgb-slider-arrow--prev');
    const nextBtn = root.querySelector<HTMLButtonElement>('.stgb-slider-arrow--next');
    const status = root.querySelector<HTMLElement>('.stgb-slider-status');

    if (!track || slides.length === 0) return;

    root.dataset[INIT_FLAG] = 'true';

    const total = slides.length;
    const loop = root.dataset.loop === 'true';
    const autoplay = root.dataset.autoplay === 'true';
    const speed = parseInt(root.dataset.autoplaySpeed || '5000', 10) || 5000;
    const statusTemplate = (status && status.dataset.template) || 'Slide %1$s of %2$s';

    // A live query (not a one-off boolean) so a mid-session OS reduced-motion
    // toggle is respected.
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    let index = 0;
    let timer: number | undefined;
    let isHovering = false;
    let isFocused = false;

    // Off-screen slides are rendered with a data-bg instead of an inline
    // background-image; swap it in on demand so distant slides stay unfetched
    // while the active slide + its neighbours are always ready (no flash).
    const loadSlideImage = (i: number) => {
        const el = slides[i]?.querySelector<HTMLElement>('.stgb-slider-image[data-bg]');
        if (!el || !el.dataset.bg) return;
        el.style.backgroundImage = `url("${el.dataset.bg}")`;
        el.removeAttribute('data-bg');
    };

    const render = (announce: boolean) => {
        // If the user navigates while focus is on a control inside a slide, that
        // slide is about to become inert (which blurs it); remember so we can
        // land focus on the new slide instead of dropping it to <body>.
        const focusWasInTrack = track.contains(document.activeElement);

        track.style.transform = `translateX(-${index * 100}%)`;

        loadSlideImage(index);
        loadSlideImage((index + 1) % total);
        loadSlideImage((index - 1 + total) % total);

        dots.forEach((dot, i) => {
            const isActive = i === index;
            dot.classList.toggle('is-active', isActive);
            dot.setAttribute('aria-current', isActive ? 'true' : 'false');
        });

        slides.forEach((slide, i) => {
            const isActive = i === index;
            slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
            // `inert` removes off-screen slides from the tab order AND the a11y
            // tree and blurs any focus they hold — so freeform overlay content
            // (links/buttons) can't be tabbed into, nor leave a focused node
            // inside an aria-hidden subtree. tabIndex is kept as a fallback for
            // browsers without inert support.
            slide.inert = !isActive;
            slide.querySelectorAll<HTMLElement>('a, button, input, select, textarea').forEach((el) => {
                el.tabIndex = isActive ? 0 : -1;
            });
        });

        // Making the departing slide inert drops focus to <body>; move it to the
        // now-active slide so keyboard users keep a sensible position.
        if (focusWasInTrack && !track.contains(document.activeElement)) {
            const active = slides[index];
            if (active) {
                active.tabIndex = -1;
                active.focus({ preventScroll: true });
            }
        }

        // Announce the change to screen readers — but not on the initial paint,
        // where the server-rendered text already matches.
        if (announce && status) {
            status.textContent = statusTemplate
                .replace('%1$s', String(index + 1))
                .replace('%2$s', String(total));
        }
    };

    const goTo = (next: number) => {
        if (next < 0) {
            index = loop ? total - 1 : 0;
        } else if (next >= total) {
            index = loop ? 0 : total - 1;
        } else {
            index = next;
        }
        render(true);
    };

    const next = () => goTo(index + 1);
    const prev = () => goTo(index - 1);

    const stop = () => {
        if (timer !== undefined) {
            window.clearInterval(timer);
            timer = undefined;
        }
    };

    // Autoplay runs only when it is enabled, motion is allowed, there is more
    // than one slide, and the user is not currently interacting (hover / focus)
    // nor the tab hidden. Centralising these guards here keeps the pause
    // invariant intact no matter which event triggered a (re)start.
    const maybeStart = () => {
        if (!autoplay || motionQuery.matches || total < 2) return;
        if (isHovering || isFocused || document.hidden) return;
        stop();
        timer = window.setInterval(next, speed);
    };

    // Restart the countdown after a manual interaction so the next auto-advance
    // is a full interval away.
    const restart = () => {
        if (!autoplay) return;
        stop();
        maybeStart();
    };

    nextBtn?.addEventListener('click', () => { next(); restart(); });
    prevBtn?.addEventListener('click', () => { prev(); restart(); });

    // Touch swipe (mobile): mirror the arrow controls.
    addSwipe(root, {
        onNext: () => { next(); restart(); },
        onPrev: () => { prev(); restart(); },
    });
    dots.forEach((dot, i) => {
        dot.addEventListener('click', () => { goTo(i); restart(); });
    });

    root.addEventListener('keydown', (event) => {
        // Don't hijack modified arrow keys (Shift+Arrow selection, OS shortcuts).
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (event.key === 'ArrowRight') {
            next();
            restart();
        } else if (event.key === 'ArrowLeft') {
            prev();
            restart();
        }
    });

    // Pause autoplay while the user is interacting or the tab is hidden, and
    // resume only when none of those conditions hold (via maybeStart).
    root.addEventListener('mouseenter', () => { isHovering = true; stop(); });
    root.addEventListener('mouseleave', () => { isHovering = false; maybeStart(); });
    root.addEventListener('focusin', () => { isFocused = true; stop(); });
    root.addEventListener('focusout', () => { isFocused = false; maybeStart(); });
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            stop();
        } else {
            maybeStart();
        }
    });
    motionQuery.addEventListener('change', () => {
        if (motionQuery.matches) {
            stop();
        } else {
            maybeStart();
        }
    });

    render(false);
    maybeStart();
};

const initAll = () => {
    // The front-end script is also enqueued in the editor; skip it there so it
    // never fights with the React-rendered editor preview.
    if (document.body.classList.contains('wp-admin')) return;
    document.querySelectorAll<HTMLElement>('.stgb-slider').forEach(initSlider);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
} else {
    initAll();
}

// Mark this side-effect file as a module so its top-level constants are
// module-scoped (avoids a global-scope name clash with other blocks' react.ts).
export {};
